import { useCallback, useEffect, useState } from 'react'

/**
 * Generic hook for managing localStorage with automatic JSON serialization/deserialization
 *
 * @param key - The localStorage key
 * @param initialValue - The initial value if nothing is stored
 * @returns [value, setValue, removeValue] - Current value, setter function, and remove function
 *
 * @example
 * ```tsx
 * const [user, setUser, removeUser] = useLocalStorage('user', { name: '' })
 * setUser({ name: 'John' })
 * removeUser()
 * ```
 */
export function useLocalStorage<T>(
    key: string,
    initialValue: T,
): [T, (value: T) => void, () => void] {
    // State to store our value
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            const item = window.localStorage.getItem(key)
            return item ? JSON.parse(item) : initialValue
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error)
            return initialValue
        }
    })

    // Return a wrapped version of useState's setter function that
    // persists the new value to localStorage
    const setValue = useCallback(
        (value: T) => {
            try {
                setStoredValue(value)
                window.localStorage.setItem(key, JSON.stringify(value))
            } catch (error) {
                console.warn(`Error setting localStorage key "${key}":`, error)
            }
        },
        [key],
    )

    // Function to remove the value from localStorage
    const removeValue = useCallback(() => {
        try {
            window.localStorage.removeItem(key)
            setStoredValue(initialValue)
        } catch (error) {
            console.warn(`Error removing localStorage key "${key}":`, error)
        }
    }, [key, initialValue])

    // Listen for changes to localStorage from other tabs/windows
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === key && e.newValue) {
                try {
                    setStoredValue(JSON.parse(e.newValue))
                } catch (error) {
                    console.warn(
                        `Error parsing localStorage change for key "${key}":`,
                        error,
                    )
                }
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [key])

    return [storedValue, setValue, removeValue]
}

/**
 * Utility functions for direct localStorage access with JSON serialization
 * Use these when you don't need React state management
 */
export const localStorage = {
    /**
     * Get an item from localStorage with automatic JSON parsing
     */
    getItem: <T>(key: string, defaultValue: T): T => {
        try {
            const item = window.localStorage.getItem(key)
            return item ? JSON.parse(item) : defaultValue
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error)
            return defaultValue
        }
    },

    /**
     * Set an item in localStorage with automatic JSON serialization
     */
    setItem: <T>(key: string, value: T): void => {
        try {
            window.localStorage.setItem(key, JSON.stringify(value))
        } catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error)
        }
    },

    /**
     * Remove an item from localStorage
     */
    removeItem: (key: string): void => {
        try {
            window.localStorage.removeItem(key)
        } catch (error) {
            console.warn(`Error removing localStorage key "${key}":`, error)
        }
    },
}
